import { MenuContent } from './MenuContent';
import { FC, SVGProps, useState } from 'react';
import clsx from 'clsx';
import {
  MenuTrigger,
  Popover,
  Menu,
  type PopoverProps,
  type MenuTriggerProps,
  type MenuProps as AriaMenuProps,
} from 'react-aria-components';
import TaillessLineArrowUp1 from '@streamlinehq/streamlinehq/img/streamline/tailless-line-arrow-up-1.svg';
import TaillessLineArrowDown1 from '@streamlinehq/streamlinehq/img/streamline/tailless-line-arrow-down-1.svg';
import { Button, type ButtonInternalProps } from '../../button';
import commonStyles from '../Dropdowns.module.css';

type MenuProps<T extends object> = Pick<
  AriaMenuProps<T>,
  'children' | 'selectedKeys' | 'onSelectionChange' | 'selectionMode' | 'shouldCloseOnSelect'
>;

export type Props<T extends object> = MenuProps<T> &
  Pick<PopoverProps, 'placement'> &
  Pick<MenuTriggerProps, 'isOpen' | 'defaultOpen' | 'onOpenChange'> &
  Pick<ButtonInternalProps, 'size' | 'variant' | 'color' | 'isDisabled' | 'isPending' | 'startIcon'> & {
    label: string;
    isLabelVisuallyHidden?: boolean;
    shouldMatchTriggerWidth?: boolean;
    icon?: (args: { isOpen: boolean }) => FC<SVGProps<SVGSVGElement>>;
  };

export function MenuButton<T extends object>(props: Props<T>) {
  const {
    children,

    /** MenuTrigger props */
    isOpen: isOpenFromProps,
    onOpenChange,
    defaultOpen,

    variant,
    color,
    size,
    isDisabled,
    isPending,
    label,
    isLabelVisuallyHidden,
    startIcon,
    icon: IconFromProps,

    placement,
    selectedKeys,
    onSelectionChange,
    selectionMode,
    shouldCloseOnSelect,
    shouldMatchTriggerWidth,
  } = props;
  const isControled = typeof isOpenFromProps === 'boolean' && onOpenChange;
  const [isOpenFromState, setIsOpenFromState] = useState<boolean>(
    isControled ? isOpenFromProps : (defaultOpen ?? false),
  );
  const isOpen = isControled ? isOpenFromProps : isOpenFromState;
  const Icon = IconFromProps ? IconFromProps({ isOpen }) : isOpen ? TaillessLineArrowUp1 : TaillessLineArrowDown1;

  return (
    <div className={commonStyles.wrapper} data-label-visually-hidden={isLabelVisuallyHidden ? 'true' : undefined}>
      <MenuTrigger
        isOpen={isOpenFromProps}
        defaultOpen={defaultOpen}
        onOpenChange={nextOpen => {
          if (isControled) {
            onOpenChange(nextOpen);
          } else {
            setIsOpenFromState(nextOpen);
            onOpenChange?.(nextOpen);
          }
        }}
      >
        <Button
          variant={variant}
          color={color}
          size={size}
          isDisabled={isDisabled}
          isPending={isPending}
          startIcon={startIcon}
          endIcon={Icon}
          isContentVisuallyHidden={isLabelVisuallyHidden}
        >
          {label}
        </Button>
        <Popover
          shouldFlip
          className={clsx(commonStyles.popover, shouldMatchTriggerWidth && commonStyles['match-trigger-width'])}
          placement={placement}
        >
          <Menu
            selectionMode={selectionMode}
            selectedKeys={selectedKeys}
            onSelectionChange={onSelectionChange}
            className={commonStyles.list}
            shouldCloseOnSelect={shouldCloseOnSelect === undefined ? !selectionMode : shouldCloseOnSelect}
          >
            {typeof children === 'function' ? (
              children
            ) : (
              <MenuContent selectionMode={selectionMode}>{children}</MenuContent>
            )}
          </Menu>
        </Popover>
      </MenuTrigger>
    </div>
  );
}
